import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1519121785383-3229633bb75b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHx5ZWxsb3clMjBtb2Rlcm4lMjB0YXhpJTIwY2l0eSUyMHN0cmVldHxlbnwwfHx8fDE3ODA1MDI4NTF8MA&ixlib=rb-4.1.0&q=85";
const PREMIUM_IMG = "https://images.pexels.com/photos/36498953/pexels-photo-36498953.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const MAP_IMG = "https://images.unsplash.com/photo-1664044300566-93c1ce51aa2e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGNpdHklMjBtYXAlMjB2aWV3fGVufDB8fHx8MTc4MDUwMjg1MXww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const go = () => {
    if (!user) navigate("/login");
    else if (user.role === "admin") navigate("/admin");
    else if (user.role === "driver") navigate("/driver");
    else navigate("/passenger");
  };

  return (
    <div className="min-h-screen bg-white text-black" data-testid="landing-page">
      <nav className="border-b-2 border-black px-6 md:px-12 py-4 flex justify-between items-center bg-white sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-[#FFD600] border-2 border-black flex items-center justify-center font-display text-xl">T</div>
          <span className="font-display text-2xl">TAXI/X</span>
        </div>
        <div className="flex gap-3 items-center">
          {user ? (
            <button data-testid="nav-dashboard-btn" onClick={go} className="brut-btn">Tableau de bord</button>
          ) : (
            <>
              <Link data-testid="nav-login-link" to="/login" className="font-mono-eyebrow hidden sm:block hover:underline">Connexion</Link>
              <Link data-testid="nav-signup-btn" to="/register" className="brut-btn">S'inscrire →</Link>
            </>
          )}
        </div>
      </nav>

      <section className="px-6 md:px-12 py-12 md:py-20 grid md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-7">
          <p className="font-mono-eyebrow text-[#52525B]">// Course en temps réel · Genève · Vaud</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-4 leading-[0.95]">
            RÉSERVEZ UNE COURSE.<br/>
            <span className="bg-[#FFD600] px-2 inline-block border-2 border-black mt-2">ZÉRO STRESS.</span>
          </h1>
          <p className="text-lg mt-6 max-w-xl text-[#52525B]">
            Appelez un chauffeur en quelques secondes. Suivez-le sur une carte en direct.
            Payez en espèces, par carte, ou en un tap. Conçu pour passagers, chauffeurs et flottes.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <button data-testid="hero-get-started-btn" onClick={go} className="brut-btn text-lg">Commencer →</button>
            <Link data-testid="hero-driver-link" to="/register?role=driver" className="brut-btn brut-btn-secondary text-lg">Devenir chauffeur</Link>
          </div>
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            {[["50k+", "Courses"], ["4.9★", "Note"], ["24/7", "Service"]].map(([n, l]) => (
              <div key={l} className="border-l-2 border-black pl-3">
                <div className="font-display text-2xl">{n}</div>
                <div className="font-mono-eyebrow text-[#52525B]">{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-5 relative">
          <div className="border-2 border-black overflow-hidden shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
            <img src={HERO_IMG} alt="Taxi" className="w-full h-[420px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-[#FFD600] border-2 border-black p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hidden md:block">
            <p className="font-mono-eyebrow">Temps moyen</p>
            <p className="font-display text-3xl">4 MIN</p>
          </div>
        </div>
      </section>

      <section className="px-6 md:px-12 py-16 bg-[#F4F4F5] border-t-2 border-b-2 border-black">
        <p className="font-mono-eyebrow">// Fonctionnalités</p>
        <h2 className="font-display text-4xl sm:text-5xl mt-2">TOUT CE QU'IL VOUS FAUT</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {[
            { t: "SUIVI EN DIRECT", d: "Voyez votre chauffeur approcher en temps réel sur la carte.", n: "01" },
            { t: "TARIF JUSTE", d: "Estimation transparente avant la réservation. Aucune surprise.", n: "02" },
            { t: "CHOIX DU VÉHICULE", d: "Standard, Premium ou XL — choisissez selon votre besoin.", n: "03" },
            { t: "NOTATIONS", d: "Système d'évaluation à deux sens pour garantir la qualité.", n: "04" },
            { t: "PAIEMENT FLEXIBLE", d: "Espèces, carte via Stripe (CHF), ou intégré dans l'app.", n: "05" },
            { t: "MODE CHAUFFEUR", d: "Passez en mode chauffeur, acceptez des courses, gagnez votre vie.", n: "06" },
          ].map((f) => (
            <div key={f.t} className="brut-card p-6">
              <span className="font-mono-eyebrow text-[#52525B]">{f.n}</span>
              <h3 className="font-display text-2xl mt-2">{f.t}</h3>
              <p className="mt-3 text-[#52525B]">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 py-16 grid md:grid-cols-2 gap-12 items-center">
        <div>
          <p className="font-mono-eyebrow">// Catégories</p>
          <h2 className="font-display text-4xl sm:text-5xl mt-2">CHOISISSEZ.</h2>
          <div className="mt-6 space-y-3">
            {[
              { name: "STANDARD", price: "CHF 3.20/km", desc: "Berline 1–4 places." },
              { name: "PREMIUM", price: "CHF 3.20/km", desc: "Haut de gamme 1–4 places." },
              { name: "XL", price: "CHF 3.20/km", desc: "Van jusqu'à 6 passagers." },
            ].map((t) => (
              <div key={t.name} className="brut-card p-4 flex justify-between items-center">
                <div>
                  <div className="font-display text-xl">{t.name}</div>
                  <div className="text-sm text-[#52525B]">{t.desc}</div>
                </div>
                <span className="brut-badge">{t.price}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <img src={PREMIUM_IMG} alt="Premium" className="border-2 border-black w-full h-72 object-cover shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]" />
          <img src={MAP_IMG} alt="Carte" className="border-2 border-black w-full h-72 object-cover translate-y-8 shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]" />
        </div>
      </section>

      <section className="px-6 md:px-12 py-20 bg-black text-white text-center grain">
        <p className="font-mono-eyebrow text-[#FFD600]">// Prêt à partir</p>
        <h2 className="font-display text-5xl sm:text-7xl mt-4">ALLEZ LOIN.<br/><span className="text-[#FFD600]">PLUS VITE.</span></h2>
        <button data-testid="cta-bottom-btn" onClick={go} className="brut-btn mt-8 text-lg">Ouvrir l'app →</button>
      </section>

      <footer className="px-6 md:px-12 py-8 border-t-2 border-black flex justify-between items-center">
        <span className="font-mono-eyebrow">© TAXI/X 2026</span>
        <span className="font-mono-eyebrow">/ FAIT AVEC RAPIDITÉ</span>
      </footer>
    </div>
  );
}
